/* eslint-disable react/prefer-stateless-function */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    description = "description",
    name = "name",
    position = "position",
    email = "email",
    phoneNumber = "phoneNumber",
    region = "region",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.description]: string;
        [fields.name]: string;
        [fields.position]: string;
        [fields.email]: string;
        [fields.phoneNumber]: string;
        [fields.region]: string;
    };
}

export interface MarketContactStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    heading?: InjectableCss;
    description?: InjectableCss;
    email?: InjectableCss;
    phoneNumber?: InjectableCss;
    wrapper?: InjectableCss;
}

export const marketContactStyle: MarketContactStyles = {
    container: {
        gap: 20,
    },
    gridItem: {
        width: [12, 12, 4, 4, 4],
    },
    heading: {
        css: css`
            font-size: 16px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 24px;
            color: #33333a;
            display: block;
        `,
    },

    description: {
        css: css`
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 24px;
            color: #33333a;
            display: block;
            padding-right: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-right: 0px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },

    email: {
        css: css`
            font-size: 16px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 18px;
            color: #006bc0;
        `,
    },

    phoneNumber: {
        css: css`
            padding: 20px 0 5px 0;
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 24px;
            color: #33333a;
        `,
    },

    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 20px;
                    `,
                    css`
                        padding-bottom: 20px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = marketContactStyle;
type Props = OwnProps;

class IntMarketContact extends React.Component<Props, any> {
    // eslint-disable-next-line no-useless-constructor
    constructor(props: Props) {
        super(props);
    }
    render() {
        const fields = this.props.fields;
        return (
            <>
                <StyledWrapper {...styles.wrapper} id="parent_wrapper">
                    <StyledWrapper {...styles.heading}>{fields?.heading}</StyledWrapper>
                    <StyledWrapper {...styles.description}>{parse(fields.description, parserOptions)}</StyledWrapper>
                    <StyledWrapper {...styles.description}>{parse(fields.region, parserOptions)}</StyledWrapper>

                    {fields?.phoneNumber && (
                        <Link href={`tel:${fields?.phoneNumber}`} {...styles.phoneNumber} target="_blank">
                            {fields?.phoneNumber}
                        </Link>
                    )}
                    <StyledWrapper
                        id="wrapper"
                        css={css`
                            padding-top: 25px;
                        `}
                    >
                        <StyledWrapper {...styles.description}>
                            {fields?.name}
                            {fields?.position && <Typography>,&nbsp;{fields?.position}</Typography>}
                        </StyledWrapper>
                        {fields?.email && (
                            <Link href={`mailto:${fields?.email}`} {...styles.email} target="_blank">
                                {fields?.email}
                            </Link>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </>
        );
    }
}

const divider: WidgetModule = {
    component: IntMarketContact,
    definition: {
        group: "Basic",
        displayName: "Market Contact us",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                extendedConfig: { height: 100 },
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 3,
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : " The HTML contains invalid styles that prevent it from being saved";
                },
            },
            {
                name: fields.name,
                displayName: "Name",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 2,
                fieldType: "Translatable",
            },
            {
                name: fields.position,
                displayName: "Position",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 3,
                fieldType: "Translatable",
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 4,
                fieldType: "Translatable",
            },
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 5,
                fieldType: "General",
            },
            {
                name: fields.region,
                displayName: "Region",
                editorTemplate: "RichTextField",
                extendedConfig: { height: 100 },
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 6,
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : " The HTML contains invalid styles that prevent it from being saved";
                },
            },
        ],
    },
};

export default divider;
